import type { IPerson } from "../../../../interfaces/models/IPerson";
import type {
  IMemberPersonView,
  IPersonView,
} from "../../../../interfaces/models/IPersonView";
import type { IPersonCreate } from "../../../../interfaces/models/IPersonCreate";
import { MemberType } from "../../../../Enums/PersonEnums";
import { normalizeRoleToKey } from "../../../../Enums/UserEnums";
import {
  normalizePersonAreasOfPracticeIds,
  normalizePersonAopExpertiseIds,
} from "../../../../utils/personAopNormalize";
import {
  normalizeSocialNetworksForApi,
  normalizeSocialNetworksFromView,
} from "./socialNetworks";

/**
 * Extracts a plain string ID from an `additionalOffices` item that may have been
 * stored in various legacy formats:
 *   - string UUID (canonical)
 *   - numeric shortId
 *   - full office object (some legacy Cosmos documents stored full objects)
 */
function extractOfficeItemId(item: unknown): string {
  if (typeof item === "string") {
    return item.trim();
  }
  if (typeof item === "number") {
    return String(item);
  }
  if (item !== null && typeof item === "object") {
    const obj = item as Record<string, unknown>;
    if (typeof obj.id === "string" && obj.id.trim()) {
      return obj.id.trim();
    }
    if (obj.shortId != null) {
      return String(obj.shortId);
    }
    if (obj.legacyId != null) {
      return String(obj.legacyId);
    }
  }
  return "";
}

/** Trim, drop empties, dedupe (first wins), preserve order. */
function normalizeStringIdList(ids: string[] | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids ?? []) {
    if (typeof raw !== "string") {
      continue;
    }
    const id = raw.trim();
    if (!id) {
      continue;
    }
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

function trimOptionalRank(v: string | null | undefined): string | undefined {
  if (v == null) {
    return undefined;
  }
  const t = String(v).trim();
  return t.length > 0 ? t : undefined;
}

/**
 * Normalize memberType from API, state or form.
 * Don't use `Number(raw)` blindly: `Number(true) === 1` was saved as Non Member by error.
 * Unrecognized values → Member (0), which is the default product in Manage Firm.
 */
function coerceMemberType(raw: unknown): MemberType {
  if (
    raw === MemberType.member ||
    raw === MemberType.nonMember ||
    raw === MemberType.staff
  ) {
    return raw;
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t === "0" || t === "1" || t === "2") {
      return Number(t) as MemberType;
    }
  }
  if (
    typeof raw === "number" &&
    Number.isInteger(raw) &&
    (raw === 0 || raw === 1 || raw === 2)
  ) {
    return raw as MemberType;
  }
  return MemberType.member;
}

/**
 * Maps a profile `IPerson` (expanded `areasOfPractice` objects) to the create/edit form shape.
 * Used when editing AOPs from the profile overview without a full `IPersonView` row.
 */
export function ipersonToPersonCreate(person: IPerson): IPersonCreate {
  const areasIds = normalizeStringIdList(
    normalizePersonAreasOfPracticeIds(person),
  );
  const expertiseRaw = normalizeStringIdList(
    normalizePersonAopExpertiseIds(person),
  );
  const areaSet = new Set(areasIds);
  const expertiseIds = expertiseRaw.filter((id) => areaSet.has(id));

  const viewLike = {
    ...(person as unknown as IPersonView),
    areasOfPractice: areasIds.length ? areasIds : undefined,
    aopExpertiseIds: expertiseIds.length ? expertiseIds : undefined,
  } as unknown as IPersonView;

  return mapPersonViewToPersonCreate(viewLike);
}

/**
 * Convierte una fila/vista del API (`IMemberPersonView` | `INonMemberPersonView`)
 * al shape del formulario (`IPersonCreate`).
 */
export function mapPersonViewToPersonCreate(
  person: IPersonView,
): IPersonCreate {
  const memberTypeResolved = coerceMemberType(person.memberType);
  const isMember =
    memberTypeResolved === MemberType.member ||
    memberTypeResolved === MemberType.staff;
  const member = isMember ? (person as IMemberPersonView) : null;

  const areasCanonical = normalizeStringIdList(
    normalizePersonAreasOfPracticeIds(person as unknown as IPerson),
  );
  const expertiseCanonical = normalizeStringIdList(
    normalizePersonAopExpertiseIds(person as unknown as IPerson),
  );
  const areaSet = new Set(areasCanonical);
  const expertiseFiltered = expertiseCanonical.filter((id) => areaSet.has(id));

  const ext = person as unknown as Record<string, unknown>;
  const rawMain = ext["mainOffice"];
  const fromExt =
    typeof rawMain === "string" && rawMain.trim() !== ""
      ? rawMain.trim()
      : undefined;
  const mappedMainOffice =
    fromExt ??
    (typeof member?.mainOffice === "string" && member.mainOffice.trim() !== ""
      ? member.mainOffice.trim()
      : undefined);

  const rawTitle = ext["title"];
  const rawListTitle = ext["listTitle"];
  const mappedTitle =
    typeof rawTitle === "string" && rawTitle.trim() !== ""
      ? rawTitle.trim()
      : typeof rawListTitle === "string" && rawListTitle.trim() !== ""
        ? rawListTitle.trim()
        : "";

  return {
    memberType: memberTypeResolved,
    firm: person.firm ?? "",
    name: person.name ?? "",
    lastName: person.lastName ?? "",
    title: mappedTitle,
    unlistedTitle: person.unlistedTitle ?? "",
    unlistedTitleToggle: Boolean(String(person?.unlistedTitle ?? "").trim()),
    firmEmail: person.firmEmail ?? "",
    photo: person.photo,
    webpage: person.webpage,
    middleInitial: person.middleInitial,
    suffix: person.suffix,
    honorary: person.honorary,
    isAttorney: person.isAttorney,
    assistantName: person.assistantName,
    assistantEmail: person.assistantEmail,
    phoneNumber:
      person.phoneNumber && typeof person.phoneNumber === "object"
        ? { ...person.phoneNumber }
        : person.phoneNumber && typeof person.phoneNumber === "string"
          ? { code: "", phone: person.phoneNumber, basecode: "" }
          : undefined,
    mainOffice: mappedMainOffice,
    additionalOffices: (() => {
      // For members, read from the typed member view.
      // For non-members (memberType === 1) the field is not on IMemberPersonView, but the
      // Cosmos document may still carry it — fall back to the raw ext object, same pattern
      // used for mainOffice above.
      const rawFromMember = member?.additionalOffices as unknown[] | undefined;
      const rawFromExt =
        !rawFromMember?.length
          ? (ext["additionalOffices"] as unknown[] | undefined)
          : undefined;
      const raw = rawFromMember ?? rawFromExt;
      if (!raw?.length) {
        return undefined;
      }
      const ids = raw.map(extractOfficeItemId).filter(Boolean);
      return ids.length ? ids : undefined;
    })(),
    isSearchable:
      typeof ext["isSearchable"] === "boolean"
        ? ext["isSearchable"]
        : (member?.isSearchable ?? false),
    isWsgMember: ext["isWsgMember"] === true,
    hasAssistant: Boolean(
      person.assistantName?.trim() || person.assistantEmail?.trim(),
    ),
    socialNetworks: normalizeSocialNetworksFromView(
      person.socialNetworks ? [...person.socialNetworks] : undefined,
    ),
    yearJoinedFirm: person.yearJoinedFirm,
    bio: person.bio,
    education: person.education ?? "",
    languages: person.languages ? [...person.languages] : undefined,
    barAdmissions: person.barAdmissions,
    birthInformation: person.birthInformation,
    isln: person.isln,
    areasOfPractice: areasCanonical.length ? areasCanonical : undefined,
    aopExpertiseIds: expertiseFiltered.length ? expertiseFiltered : undefined,
    aopRankName1: trimOptionalRank(person.aopRankName1),
    aopRankName2: trimOptionalRank(person.aopRankName2),
    aopRankName3: trimOptionalRank(person.aopRankName3),
    aopRankName4: trimOptionalRank(person.aopRankName4),
    practiceChair: person.practiceChair,
    significantAccomplishments: person.significantAccomplishments,
    additionalActivities: person.additionalActivities,
    professionalActivitiesAndPreviousExperience:
      person.professionalActivitiesAndPreviousExperience,
    industryOrganizationAffiliation: person.industryOrganizationAffiliation
      ? [...person.industryOrganizationAffiliation]
      : undefined,
    additionalAffiliations: person.additionalAffiliations
      ? [...person.additionalAffiliations]
      : undefined,
    additionalInformation: person.additionalInformation,
    seminarPresentation: person.seminarPresentation,
    publications: person.publications,
    pastEmploymentPositions: person.pastEmploymentPositions,
    role: normalizeRoleToKey(
      (member?.role ?? person.role) as string | undefined,
    ),
    userId: person.userId,
    username: person.userName ?? "",
  };
}

/**
 * Arma el cuerpo para POST/PUT `/person` a partir del formulario (`IPersonCreate`)
 * y, en edición, la vista previa (`IPersonView`). Sin pasar por {@link IPerson}.
 */
export function mapPersonCreateToSavePayload(
  form: IPersonCreate,
  existing?: IPersonView,
): IPersonCreate & { id?: string } {
  const unlistedTitleOut = form.unlistedTitleToggle
    ? (form.unlistedTitle ?? "")
    : "";

  const aopIds = normalizeStringIdList(form.areasOfPractice);
  const myAopRaw = normalizeStringIdList(form.aopExpertiseIds);
  const areaSet = new Set(aopIds);
  const myAop = myAopRaw.filter((id) => areaSet.has(id));

  const payload: IPersonCreate & { id?: string } = {
    ...form,
    memberType: coerceMemberType(form.memberType),
    unlistedTitle: unlistedTitleOut,
    socialNetworks: normalizeSocialNetworksForApi(form.socialNetworks),
    areasOfPractice: aopIds,
    aopExpertiseIds: myAop,
    aopRankName1: trimOptionalRank(form.aopRankName1),
    aopRankName2: trimOptionalRank(form.aopRankName2),
    aopRankName3: trimOptionalRank(form.aopRankName3),
    aopRankName4: trimOptionalRank(form.aopRankName4),
  };

  if (aopIds.length === 0) {
    payload.isSearchable = false;
  }

  if (existing?.id) {
    payload.id = existing.id;
  }

  const existingPhoto = String(
    (existing as { photo?: string } | undefined)?.photo ?? "",
  ).trim();
  if (existingPhoto && !String(form.photo ?? "").trim()) {
    payload.photoRemoved = true;
  }

  if (typeof form.photo === "string" && form.photo.startsWith("data:")) {
    payload.photoFileName = "photo";
    payload.photoExtension = "jpg";
  }

  const legacy = (existing as { legacyId?: string })?.legacyId;
  if (legacy != null && String(legacy).trim() !== "") {
    payload.legacyId = String(legacy).trim();
  }

  const webpage = String(
    form.webpage ?? (existing as { webpage?: string } | undefined)?.webpage ?? "",
  ).trim();
  if (webpage) {
    payload.webpage = webpage;
  }

  return payload;
}
